import React, { useState, useEffect, useRef, useMemo } from 'react';
import AceEditor from 'react-ace';
import { IconButton } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import Alert from '@material-ui/lab/Alert';
import { Code, Dashboard } from '@material-ui/icons';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-github_dark';
import 'ace-builds/webpack-resolver';

import usePreferences from 'src/hooks/usePreferences';
import { useKeyboardShortcuts, useNotif, useToggle } from 'src/hooks';
import { FormatAlignRight } from '@material-ui/icons';
import JsonUIModeEditor from './JsonUIModeEditor';
const Editor = ({
  value = '{}',
  onChange = () => {},
  wrapperStyle,
  save = () => {},
  delay = 200,
  mode = 'json',
  readOnly = true,
  ...props
}) => {
  const { preferences } = usePreferences();
  const notif = useNotif();
  const [
    isFullScreen,
    fullsreenOn,
    fullscreenOff,
    toggleFullscreen
  ] = useToggle(false);
  const [isReadOnly, , , toggleReadOnly] = useToggle(readOnly);
  const [isFormatted, format, , toggleFormat] = useToggle(false);
  const [editorValue, setEditorValue] = useState(value);
  const timeoutRef = useRef(null);
  const [error, setError] = useState({ status: false, msg: '' });
  const [uiMode, setUIMode] = useState(false);

  const formatJson = json => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch (err) {
      setError({
        status: true,
        msg: 'Invalid JSON format: ' + err.message
      });
      return json;
    }
  };

  const convertCommentsToData = input => {
    // Detect existing _comment<number> keys
    const existingMatches = input.match(/"_comment(\d+)":/g) || [];
    let maxIndex = 0;
    existingMatches.forEach(match => {
      const num = parseInt(match.match(/\d+/)[0], 10);
      if (num > maxIndex) maxIndex = num;
    });
    let commentIndex = maxIndex + 1;

    // Track whether we're inside a string
    let insideString = false;
    let escaped = false;
    let result = '';
    let i = 0;

    while (i < input.length) {
      const char = input[i];

      if (char === '"' && !escaped) {
        insideString = !insideString; // toggle string state
        result += char;
      } else if (!insideString) {
        // handle block comments
        if (char === '/' && input[i + 1] === '*') {
          let j = i + 2;
          while (
            j < input.length &&
            !(input[j] === '*' && input[j + 1] === '/')
          )
            j++;
          const comment = input
            .slice(i + 2, j)
            .split('\n')
            .map(l => l.trim())
            .filter(Boolean);
          const objectLines = comment
            .map((line, idx) => `"line${idx + 1}": ${JSON.stringify(line)}`)
            .join(', ');
          result += `"${`_comment${commentIndex++}`}": { ${objectLines} },`;
          i = j + 1; // skip past */
        }
        // handle single-line comments
        else if (char === '/' && input[i + 1] === '/') {
          let j = i + 2;
          while (j < input.length && input[j] !== '\n') j++;
          const comment = input.slice(i + 2, j).trim();
          result += `"${`_comment${commentIndex++}`}": ${JSON.stringify(
            comment
          )},`;
          i = j - 1;
        } else {
          result += char;
        }
      } else {
        result += char; // inside string, copy literally
      }

      // handle escape
      escaped = !escaped && char === '\\';
      i++;
    }

    return result;
  };

  useEffect(() => {
    if (mode === 'json' && isFormatted) {
      // const formattedJson = formatJson(value);
      if (editorValue !== value) {
        setEditorValue(value);
      }
    } else {
      setEditorValue(value);
    }
  }, [value, mode, isFormatted]);

  const handleChange = newValue => {
    setEditorValue(newValue);

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (mode === 'json') {
        try {
          const transformed = convertCommentsToData(newValue);
          const parsedJSON = JSON.parse(transformed);

          onChange(parsedJSON);
          setError({ status: false, msg: '' });
        } catch (err) {
          setError({
            status: true,
            msg: 'Invalid JSON format: ' + err.message
          });
        }
      } else {
        setEditorValue(newValue);
        onChange({
          target: { name: props.name || mode, value: newValue }
        });
        setError({ status: false, msg: '' });
      }
    }, delay);
  };

  useEffect(() => {
    if (!readOnly) {
      toggleReadOnly();
    }
  }, [readOnly]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useKeyboardShortcuts({
    onSave: () => {
      if (error.status) {
        notif.warning('Something went wrong: Please fix the errors first');
        return;
      }
      save();
      format();
    }
  });

  const editorValueParsed = useMemo(() => {
    try {
      return JSON.parse(editorValue);
    } catch (err) {
      return {}; // fallback if JSON is invalid
    }
  }, [editorValue]);
  return (
    <>
      <div
        id="editor"
        style={{
          position: isFullScreen ? 'fixed' : 'relative',
          width: isFullScreen ? '100vw' : '100%',
          height: isFullScreen ? '100vh' : '600px',
          flexGrow: 1,
          transition: 'all 0.3s ease',
          top: isFullScreen ? 0 : 'auto',
          left: isFullScreen ? 0 : 'auto',
          zIndex: isFullScreen ? 1300 : 'auto',
          ...wrapperStyle
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') fullscreenOff();
        }}
      >
        {uiMode ? (
          <JsonUIModeEditor
            data={editorValueParsed || {}}
            onChange={val => {
              setEditorValue(JSON.stringify(val, null, 2));
              onChange(val);
            }}
          />
        ) : (
          <AceEditor
            mode={mode}
            theme={preferences.mode === 'dark' ? 'github_dark' : 'github'}
            value={editorValue}
            onChange={handleChange}
            fontSize={14}
            showPrintMargin={false}
            style={{ width: '100%', height: '100%' }}
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              useWorker: true,
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: true,
              enableSnippets: true,
              showPrintMargin: false,
              tabSize: 2
            }}
            readOnly={isReadOnly}
            {...props}
          />
        )}

        {/* Controls */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10
          }}
        >
          {mode === 'json' && (
            <>
              {!uiMode && (
                <IconButton onClick={format} color="primary">
                  <FormatAlignRight />
                </IconButton>
              )}
              <IconButton
                onClick={() => {
                  setUIMode(prev => !prev);
                  fullscreenOff();
                }}
                color={'secondary'}
                title={uiMode ? 'Switch to Text Mode' : 'Switch to UI Mode'}
              >
                {uiMode ? <Dashboard /> : <Code />}
                {/* Just simple icons or emojis for demo */}
              </IconButton>
            </>
          )}
          {!uiMode && (
            <IconButton onClick={toggleFullscreen} color="primary">
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </div>

        {/* Read-Only Overlay */}
        {isReadOnly && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              top: 0,
              right: 0,
              backgroundColor:
                preferences.mode === 'light'
                  ? 'rgba(0,0,0,.1)'
                  : 'rgba(255,255,255,.4 )',
              padding: '5px 10px',
              borderRadius: '5px',
              fontSize: '12px',
              fontWeight: 'bold',
              zIndex: 1250
            }}
            onDoubleClick={toggleReadOnly}
          />
        )}
      </div>

      {/* Error Display - Below Editor */}
      {error.status && (
        <div style={{ marginTop: '10px' }}>
          <Alert severity="error">{error.msg}</Alert>
        </div>
      )}
    </>
  );
};

export default Editor;
